'use client';

import { ClientSiteContentRenderer } from '@components/ClientSiteContentRenderer';
import { FooterContent } from '@components/FooterContent';
import { IconPrev } from '@components/icons/IconPrev';
import { LogInForm } from '@features/Auth';
import { ResendScreen } from '@features/AuthScreenSwitcher/components/ResendScreen';
import { useStaticContent } from '@providers/SiteDataContextProvider';
import { observer } from '@legendapp/state/react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { RecoveryForm } from './components/RecoveryForm';
import { useRootStore } from 'src/providers';
import styles from './ScreenSwitcher.module.css';

export type TpScreen = 'login' | 'recovery' | 'resend';

type TpScreenSwitcherProps = {
  children?: ReactNode;
  containerClassName?: string;
};

const LoginScreen = ({ onSetForgot }: { onSetForgot: () => void }) => {
  return (
    <>
      <h1 className={styles.title}>
        <ClientSiteContentRenderer path="pages.auth.login.header" />
      </h1>
      <p className={styles.description}>
        <ClientSiteContentRenderer path="pages.auth.login.description" />
      </p>

      <LogInForm onSetForgot={onSetForgot} />
    </>
  );
};

const RecoveryScreen = ({
  goPrevious,
  goNext,
}: {
  goPrevious: () => void;
  goNext: () => void;
}) => {
  return (
    <div className={styles.recoveryScreen}>
      <button onClick={goPrevious} className={styles.backButton}>
        <IconPrev />
      </button>

      <h1 className={styles.title}>
        <ClientSiteContentRenderer path="pages.auth.access_recovery.header" />
      </h1>
      <p className={styles.description}>
        <ClientSiteContentRenderer path="pages.auth.access_recovery.description" />
      </p>

      <RecoveryForm onSetCheckInbox={goNext} />
    </div>
  );
};

export const AuthScreenSwitcher = observer((props: TpScreenSwitcherProps) => {
  const { children, containerClassName } = props;
  const store$ = useRootStore();
  const [screen, setScreen] = useState<TpScreen>('login');
  const staticContent = useStaticContent();

  useEffect(() => {
    const dataFromStore = store$.recoveryData.get();
    const userDataFromStorage = Array.isArray(dataFromStore)
      ? dataFromStore
      : [];
    const now = Date.now();

    const hasActiveTimer = userDataFromStorage.some(
      (item: { targetTime: number }) => Number(item.targetTime) > now,
    );

    if (hasActiveTimer) {
      setScreen('resend');
    }
  }, []);

  const renderStart = useCallback(() => {
    switch (screen) {
      case 'login':
        return <LoginScreen onSetForgot={() => setScreen('recovery')} />;
      case 'recovery':
        return (
          <RecoveryScreen
            goPrevious={() => setScreen('login')}
            goNext={() => setScreen('resend')}
          />
        );
      case 'resend':
        return (
          <ResendScreen
            goPrevious={() => setScreen('recovery')}
            goNext={() => setScreen('login')}
          />
        );
      default:
        return null;
    }
  }, [screen]);

  return (
    <div className={containerClassName}>
      {children}
      <div className={styles.renderStart}>
        <div className={styles.formContainer}>{renderStart()}</div>
        <div className={styles.footer}>
          <FooterContent
            copyright={staticContent.shared.copyright.label}
            navigation={staticContent.shared.policies_links}
          />
        </div>
      </div>
    </div>
  );
});

export default AuthScreenSwitcher;
